import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import GradientBorderCard from "@/components/brand/GradientBorderCard";
import FadeUp from "@/components/brand/FadeUp";
import { FloatingAccents } from "@/components/brand/AccentShapes";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormConsentCheckbox,
    HoneypotField,
    FormSubmitButton,
    FormErrorBanner,
    FormSuccessState,
} from "@/components/forms/FormControls";
import {
    useFormSubmit,
    isNonEmpty,
    isEmail,
    isPhone,
} from "@/components/forms/useFormSubmit";
import sponsorHero from "@/assets/brand/sponsor_hero.png";

const AUDIENCE = [
    "Contractors",
    "HVAC companies",
    "Screen repair companies",
    "Roofers",
    "Plumbers",
    "Electricians",
    "Remodelers",
    "Local home service companies",
    "Local business owners",
];

const SUPPORT_BULLETS = [
    { text: "Student access to the pilot program", color: "#EC4899" },
    { text: "Guided digital skills training", color: "#8B5CF6" },
    { text: "Safe AI-assisted website building", color: "#3B82F6" },
    { text: "Program materials and setup", color: "#14B8A6" },
    { text: "Small-cohort support", color: "#FBBF24" },
    { text: "Portfolio website completion process", color: "#EC4899" },
];

const SPONSORSHIP_OPTIONS = [
    "Sponsor 1 student seat — $500",
    "Sponsor multiple student seats",
    "Discuss pilot support",
    "In-kind support",
];

const CONSENT_LABEL =
    "I understand this is a sponsorship/program support inquiry and that tax-deductible donation status should not be assumed unless confirmed in writing by Vibe for Success, Inc.";

const initialValues = {
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    businessType: "",
    sponsorshipInterest: "",
    message: "",
    consentAccepted: false,
    hp_company_website: "",
};

const validate = (v) => {
    const e = {};
    if (!isNonEmpty(v.businessName)) e.businessName = "This field is required.";
    if (!isNonEmpty(v.contactName)) e.contactName = "This field is required.";
    if (!isEmail(v.email)) e.email = "Please enter a valid email address.";
    if (!isPhone(v.phone))
        e.phone =
            "Please enter a valid phone number (digits, spaces, +, -, ()).";
    if (!isNonEmpty(v.businessType))
        e.businessType = "This field is required.";
    if (!isNonEmpty(v.sponsorshipInterest))
        e.sponsorshipInterest = "Please choose a sponsorship option.";
    if (!v.consentAccepted)
        e.consentAccepted = "Please confirm to continue.";
    return e;
};

export default function Sponsor() {
    const form = useFormSubmit({
        formType: "sponsor",
        initialValues,
        validate,
    });

    return (
        <>
            <SeoHead
                title="Sponsor a Student | Support Future Builders Lab in Lee County"
                description="A $500 sponsorship helps one Lee County high school student participate in Future Builders Lab, a 6-week creative workforce-readiness pilot."
                path="/sponsor"
                image="/sponsor_hero.png"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="sponsor-hero"
            >
                <GlowingGridBackground accent="#EC4899" opacity={0.16} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <FadeUp>
                            <span className="vfs-eyebrow">Sponsor a Student</span>
                            <h1 className="vfs-h1 mt-5" data-testid="sponsor-h1">
                                Sponsor a student seat in{" "}
                                <span className="vfs-grad-text">
                                    Future Builders Lab.
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed max-w-xl"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                A $500 sponsorship helps one Lee County high
                                school student participate in the 6-week pilot
                                at no cost to the student.
                            </p>
                            <div className="mt-8">
                                <a
                                    href="#sponsor-form"
                                    className="vfs-cta"
                                    data-testid="sponsor-hero-scroll-cta"
                                >
                                    Send Sponsor Inquiry
                                </a>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <div className="relative">
                                <FloatingAccents />
                                <img
                                    src={sponsorHero}
                                    alt="Illustration representing a sponsored student seat in Future Builders Lab."
                                    loading="eager"
                                    fetchPriority="high"
                                    width="900"
                                    height="700"
                                    className="relative w-full h-auto rounded-3xl"
                                    style={{
                                        boxShadow:
                                            "0 30px 80px rgba(236, 72, 153, 0.25)",
                                    }}
                                />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Why sponsorship matters */}
            <section className="vfs-section" data-testid="sponsor-why">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Why sponsorship matters</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Sponsorships make it possible for local
                                students to participate in Future Builders Lab
                                at no cost to their families. Each $500
                                sponsorship funds one student seat in the first
                                6-week pilot.
                            </p>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Sponsors aren&apos;t just funding a program —
                                they&apos;re helping a creative, curious
                                student finish something real and start
                                building digital confidence.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Audience */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="sponsor-audience"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.1} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">
                            Built for local Lee County businesses
                        </h2>
                        <p
                            className="mt-4 text-lg"
                            style={{ color: "var(--text-secondary)" }}
                        >
                            We&apos;re starting with the people who keep
                            Southwest Florida running.
                        </p>
                    </FadeUp>
                    <div className="mt-9 flex flex-wrap gap-3">
                        {AUDIENCE.map((t, i) => (
                            <FadeUp key={t} delay={i * 0.03}>
                                <span
                                    className="vfs-glass px-4 py-2.5 inline-flex items-center text-sm font-medium"
                                    style={{
                                        borderRadius: 9999,
                                        color: "var(--text-primary)",
                                    }}
                                >
                                    {t}
                                </span>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* What support helps */}
            <section className="vfs-section" data-testid="sponsor-support">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">
                                What sponsorship helps support
                            </h2>
                            <ul className="mt-8 space-y-3">
                                {SUPPORT_BULLETS.map((b) => (
                                    <li
                                        key={b.text}
                                        className="flex items-start gap-3"
                                    >
                                        <span
                                            className="mt-2 inline-block w-2.5 h-2.5 rounded-full"
                                            style={{
                                                background: b.color,
                                                boxShadow: `0 0 10px ${b.color}80`,
                                            }}
                                            aria-hidden="true"
                                        />
                                        <span
                                            className="text-base sm:text-lg"
                                            style={{
                                                color: "var(--text-primary)",
                                            }}
                                        >
                                            {b.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Disclaimer */}
            <section className="vfs-section" style={{ paddingTop: 0 }}>
                <div className="vfs-container">
                    <FadeUp>
                        <div
                            className="p-6 sm:p-7 rounded-2xl max-w-3xl"
                            style={{
                                background: "rgba(251, 191, 36, 0.06)",
                                border: "1px solid rgba(251, 191, 36, 0.28)",
                            }}
                            data-testid="sponsor-disclaimer"
                        >
                            <p
                                className="text-sm sm:text-base leading-relaxed"
                                style={{ color: "var(--text-primary)" }}
                            >
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation. Sponsorships and program support
                                help fund pilot program development and student
                                participation. Tax-deductible donation status
                                should not be assumed unless confirmed in
                                writing by Vibe for Success, Inc.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Form */}
            <section
                id="sponsor-form"
                className="vfs-section"
                data-testid="sponsor-form-section"
                style={{ paddingTop: 0, scrollMarginTop: "6rem" }}
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GlassCard
                            className="p-6 sm:p-10 max-w-3xl mx-auto"
                            hoverLift={false}
                        >
                            {form.success ? (
                                <FormSuccessState
                                    heading="Thank you for your interest in sponsoring a student."
                                    body={"We'll follow up by email to share next steps, including how your sponsorship will be confirmed in writing. If you have an urgent question, you can also email hello@vibeforsuccess.org."}
                                    onReset={form.reset}
                                    testId="sponsor-success"
                                />
                            ) : (
                                <form
                                    onSubmit={form.handleSubmit}
                                    noValidate
                                    data-testid="sponsor-form"
                                >
                                    <h2 className="vfs-h2 mb-3">
                                        Sponsor inquiry
                                    </h2>
                                    <p
                                        className="text-sm mb-7"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        Tell us a little about your business —
                                        we&apos;ll be in touch by email.
                                    </p>
                                    <FormErrorBanner
                                        message={form.serverError}
                                    />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <FormField
                                            id="businessName"
                                            name="businessName"
                                            label="Business name"
                                            required
                                            autoComplete="organization"
                                            maxLength={200}
                                            value={form.values.businessName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.businessName}
                                            testId="sponsor-business-name"
                                        />
                                        <FormField
                                            id="contactName"
                                            name="contactName"
                                            label="Contact name"
                                            required
                                            autoComplete="name"
                                            maxLength={100}
                                            value={form.values.contactName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.contactName}
                                            testId="sponsor-contact-name"
                                        />
                                        <FormField
                                            id="email"
                                            name="email"
                                            type="email"
                                            label="Email"
                                            required
                                            autoComplete="email"
                                            value={form.values.email}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.email}
                                            testId="sponsor-email"
                                            inputMode="email"
                                        />
                                        <FormField
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            label="Phone"
                                            required
                                            autoComplete="tel"
                                            value={form.values.phone}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.phone}
                                            testId="sponsor-phone"
                                            inputMode="tel"
                                        />
                                        <FormField
                                            id="businessType"
                                            name="businessType"
                                            label="Business type"
                                            required
                                            placeholder="e.g. HVAC"
                                            maxLength={100}
                                            value={form.values.businessType}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.businessType}
                                            testId="sponsor-business-type"
                                        />
                                        <FormSelect
                                            id="sponsorshipInterest"
                                            name="sponsorshipInterest"
                                            label="Sponsorship interest"
                                            required
                                            options={SPONSORSHIP_OPTIONS}
                                            value={
                                                form.values.sponsorshipInterest
                                            }
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={
                                                form.errors.sponsorshipInterest
                                            }
                                            testId="sponsor-interest"
                                        />
                                    </div>
                                    <div className="mt-5">
                                        <FormTextarea
                                            id="message"
                                            name="message"
                                            label="Message"
                                            placeholder="Anything you'd like us to know (optional)"
                                            value={form.values.message}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.message}
                                            testId="sponsor-message"
                                        />
                                    </div>
                                    <div className="mt-6">
                                        <FormConsentCheckbox
                                            id="consentAccepted"
                                            name="consentAccepted"
                                            label={CONSENT_LABEL}
                                            checked={form.values.consentAccepted}
                                            onChange={form.handleChange}
                                            error={form.errors.consentAccepted}
                                            testId="sponsor-consent"
                                        />
                                    </div>

                                    <HoneypotField
                                        value={form.values.hp_company_website}
                                        onChange={form.handleChange}
                                    />

                                    <div className="mt-8">
                                        <FormSubmitButton
                                            submitting={form.submitting}
                                            testId="sponsor-submit"
                                        >
                                            Send Sponsor Inquiry
                                        </FormSubmitButton>
                                    </div>
                                </form>
                            )}
                        </GlassCard>
                    </FadeUp>
                </div>
            </section>

            {/* Bottom CTA */}
            <section
                className="vfs-section"
                data-testid="sponsor-bottom-cta"
                style={{ paddingTop: 0 }}
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GradientBorderCard radius={24}>
                            <div className="p-8 sm:p-12 text-center">
                                <h2 className="vfs-h2 mb-6">
                                    Looking for a different path?
                                </h2>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Link
                                        to="/partner"
                                        className="vfs-cta-outline"
                                        data-testid="sponsor-bottom-partner"
                                    >
                                        Partner with us
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="vfs-cta-outline"
                                        data-testid="sponsor-bottom-contact"
                                    >
                                        Have a different question?
                                    </Link>
                                </div>
                            </div>
                        </GradientBorderCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
